import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Stack,
  AppBar as MuiAppBar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import {
  getCurrentBrand,
  getBrandHeaderLogo,
  getBrandHeaderSmallLogo,
} from "@/config/brands";
import { useUser } from "@/contexts/UserContext";
import { useThemeMode } from "@/contexts/ThemeContext";

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
}));

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: apiUser, loading: userLoading } = useUser(); // Get user from API via UserContext
  const theme = useTheme();
  const brand = getCurrentBrand();
  const { mode, toggleColorMode } = useThemeMode();
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);

  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith("/dashboard");

  // Determine header background color based on theme mode
  const getHeaderBackground = () => {
    if (mode === "dark") {
      // In dark mode, use theme's paper background for consistency
      return theme.palette.background.paper;
    }
    // In light mode, use brand colors
    return brand.name === "File Florida Co" ||
      brand.name === "US Filing Services" ||
      brand.name === "File North Carolina" ||
      brand.name === "BryteBridge" ||
      brand.name === "501 Center"
      ? brand.headerPrimaryColor
      : `linear-gradient(135deg, ${brand.primaryColor} 0%, ${brand.secondaryColor} 100%)`;
  };

  // Determine header text color based on theme mode
  const getHeaderTextColor = () => {
    if (mode === "dark") {
      // In dark mode, use light text for contrast
      return theme.palette.text.primary;
    }
    // In light mode, use brand colors
    return brand.headerNavigationPrimaryColor;
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    navigate("/logout");
  };

  const handleToggleDarkMode = (event) => {
    event.stopPropagation();
    toggleColorMode();
    // Don't close the menu so user can see the change
  };

  return (
    <AppBar position="absolute">
      <Toolbar
        sx={{
          pr: "24px",
          background: getHeaderBackground(),
          color: getHeaderTextColor(),
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: isAdminRoute ? "48px !important" : undefined,
          height: isAdminRoute ? "48px" : undefined,
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src={getBrandHeaderLogo(mode)}
            alt={brand.name}
            sx={{
              display: { xs: "none", sm: "block" },
              // height: "35px",
              maxWidth: "200px",
              objectFit: "contain",
            }}
          />
          <Box
            component="img"
            src={getBrandHeaderSmallLogo(mode)}
            alt={brand.name}
            sx={{
              display: { xs: "block", sm: "none" },
              height: "35px",
              maxWidth: "200px",
              objectFit: "contain",
            }}
          />
        </Box>
        <IconButton color="inherit" onClick={handleUserMenuOpen} sx={{ ml: 1 }}>
          <AccountCircleIcon />
        </IconButton>

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 240,
                mt: 1.5,
              },
            },
          }}
        >
          {userLoading ? (
            <MenuItem>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </MenuItem>
          ) : (
            <MenuItem disabled>
              <Stack direction="row" spacing={1}>
                <AccountCircleIcon fontSize="small" />
                <Box>
                  <Typography>User</Typography>
                  <Typography
                    variant="body2"
                    sx={{ display: "block", width: "100%" }}
                    color="text.secondary"
                  >
                    {apiUser?.email || ""}
                  </Typography>
                </Box>
              </Stack>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleToggleDarkMode}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {mode === "dark" ? (
                <LightModeIcon fontSize="small" />
              ) : (
                <DarkModeIcon fontSize="small" />
              )}
              <Typography>
                {mode === "dark" ? "Light Mode" : "Dark Mode"}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>Log Out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
